import type { InfiniteData, UseSuspenseInfiniteQueryOptions } from '@tanstack/react-query';
import { useSuspenseInfiniteQuery, useSuspenseQuery } from '@tanstack/react-query';
import type { Account } from '../types';
import queryKeys from './queryKeys';
import type { QueryConfig } from 'lib/react-query';
import { apiClient } from 'lib/api';
import type { ListResponse, SortingParams } from 'types/shared';
import queryString from 'query-string';

export interface GetAccountsParams extends Partial<SortingParams> {
  name?: string;
  apps?: string;
  limit?: number;
  offset?: number;
}

export const getAccounts = async (
  params?: GetAccountsParams,
  signal?: AbortSignal,
): Promise<ListResponse<Account>> => {
  const res = await apiClient.get<ListResponse<Account>>(queryKeys.accounts, {
    params,
    signal,
    paramsSerializer: (prms) =>
      queryString.stringify(prms, { arrayFormat: 'comma' }),
  });
  return res.data;
};

interface UseAccountsOptions<TData> {
  config?: QueryConfig<typeof getAccounts, TData>;
  params?: GetAccountsParams;
}

export const useAccounts = <TData = ListResponse<Account>>
({
   params,
   config,
 }: UseAccountsOptions<TData> = {}) => {
  return useSuspenseQuery({
    staleTime: 60 * 30 * 1000,
    ...config,
    queryFn: ({ signal }) => getAccounts(params, signal),
    queryKey: [queryKeys.accounts, params],
  });
};

interface UseInfiniteAccountsOptions {
  config?: Omit<
    UseSuspenseInfiniteQueryOptions<
      ListResponse<Account>,
      unknown,
      InfiniteData<ListResponse<Account>>,
      ListResponse<Account>,
      readonly unknown[],
      number
    >,
    'queryFn' | 'queryKey' | 'initialPageParam' | 'getNextPageParam'
  >;
  params?: GetAccountsParams;
}

export const useInfiniteAccounts =
  ({
     config,
     params,
   }: UseInfiniteAccountsOptions = {}) => {
    return useSuspenseInfiniteQuery({
      ...config,
      queryFn: ({ pageParam = 1, signal }) =>
        getAccounts(
          {
            ...params,
            offset: pageParam,
            limit: params?.limit ?? 100,
          },
          signal,
        ),
      queryKey: [queryKeys.accounts, 'infinite', params],
      getNextPageParam: (lastPage, allPages) => {
        const totalItems = lastPage.count;
        const loadedItems = allPages.reduce(
          (acc, page) => acc + page.data.length,
          0,
        );

        if (loadedItems >= totalItems) {
          return undefined;
        }

        return allPages.length + 1;
      },
      initialPageParam: 1,
    });
  };